import { useMemo, useState } from 'react';
import { Card, CardBody } from '../../../components/card/card';
import { NavScrollTo } from '../../../components/nav-scroll-to/nav-scroll-to';
import useGetNtxSettings from '../../../hooks/NTX/useGetNtxSettings';
import { useTranslation } from 'react-i18next';
import ModalComponent from '../../../components/Modal/ModalComponent';
import UpdateNtxSettings from './UpdateNtxSettings';
import NtxLanguage from './Language/NtxLanguage';
import { Link } from 'react-router-dom';
import NeatixModal from '../../../components/Modal/NeatixModal';
import Loading from '@components/Shared/Loader/Loading';

const NtxSettings = () => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [settingId, setSettingId] = useState<string>();
  const [settingType, setSettingType] = useState<any>('');

  const {
    HRSettingList,
    NTXSettingList,
    FLEETSettingList,
    FinanceSettingList,
    AMESettingList,
    AttendanceSettingList,
    FixedAssetSettingList,
    InventorySettingList,
    NotificationsSettingList,
    PayablesSettingList,
    PayrollSettingList,
    PosSettingList,
    ReceivablesSettingList,
    ReportsSettingList,
    SUSettingList,
    SalesSettingList,
    loading,
    error
  } = useGetNtxSettings();

  const handleEditClick = async (id: string, setting: any) => {
    let type = getSettingType(setting);

    await setSettingId(id);
    await setSettingType(type);
    setShowModal(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const settingSections = [
    {
      id: 'NTX',
      title: 'GeneralSystem',
      icon: 'fas fa-cogs',
      settings: NTXSettingList
    },
    {
      id: 'hr',
      title: 'HumanResources',
      icon: 'fas fa-users',
      settings: HRSettingList
    },
    {
      id: 'Payroll',
      title: 'payrollManagement',
      icon: 'fas fa-money-check-alt',
      settings: PayrollSettingList
    },
    {
      id: 'RAQEEB',
      title: 'attendanceManagement',
      icon: 'fas fa-user-clock',
      settings: AttendanceSettingList
    },
    {
      id: 'AME',
      title: 'ApprovalsManagement',
      icon: 'fas fa-check-circle',
      settings: AMESettingList
    },
    {
      id: 'Sales',
      title: 'salesManagement',
      icon: 'fas fa-shopping-cart',
      settings: SalesSettingList
    },
    {
      id: 'INV',
      title: 'inventoryManagement',
      icon: 'fas fa-warehouse',
      settings: InventorySettingList
    },
    {
      id: 'Finance',
      title: 'financeManagement',
      icon: 'fas fa-chart-line',
      settings: FinanceSettingList
    },
    {
      id: 'Payables',
      title: 'payablesManagement',
      icon: 'fas fa-credit-card',
      settings: PayablesSettingList
    },
    {
      id: 'Receivables',
      title: 'receivablesManagement',
      icon: 'fas fa-hand-holding-usd',
      settings: ReceivablesSettingList
    },
    {
      id: 'FA',
      title: 'assetsManagement',
      icon: 'fas fa-boxes',
      settings: FixedAssetSettingList
    },
    {
      id: 'POS',
      title: 'posManagement',
      icon: 'fas fa-cash-register',
      settings: PosSettingList
    },
    {
      id: 'FLEET',
      title: 'FleetManagement',
      icon: 'fas fa-truck-moving',
      settings: FLEETSettingList
    },
    {
      id: 'SU',
      title: 'supportingUnits',
      icon: 'fas fa-toolbox',
      settings: SUSettingList
    },
    {
      id: 'NFT',
      title: 'notificationsSystem',
      icon: 'fas fa-bell',
      settings: NotificationsSettingList
    },
    {
      id: 'Reports',
      title: 'reportsManagement',
      icon: 'fas fa-file-alt',
      settings: ReportsSettingList
    }
  ];

  const visibleSections = useMemo(
    () => settingSections.filter(section => section.settings?.length > 0),
    [settingSections]
  );

  return (
    <div className='container p-0'>
      <div className='row justify-content-center p-0'>
        <div className='col-xl-11'>
          <Loading loading={loading} error={error}>
            <div className='row'>
              <div className='col-xl-9'>
                {/* System Language Section */}
                <div id='languages' className='mb-5'>
                  <h5>
                    <i className='fa fa-language fa-fw text-theme mb-2'></i> {t('SystemLanguage')}
                  </h5>

                  <Card>
                    <div className='list-group list-group-flush'>
                      <div className='list-group-item d-flex align-items-center'>
                        <div className='flex-1 text-break'>
                          <div>{t('Language')}</div>
                          <div className='text-inverse text-opacity-50'>
                            {t('English')}, {t('Arabic')}, {t('Turkish')}
                          </div>
                        </div>
                        <div className='flex-1 text-center text-break bg-body rounded-1 p-1 me-2 shadow border border-theme'>
                          <div className='text-theme'>{i18n.language === 'ar' ? t('Arabic') : t('English')}</div>
                        </div>
                        <div>
                          <a href='#modalLanguage' data-bs-toggle='modal' className='btn btn-sm btn-theme w-100px'>
                            {t('Change')}
                          </a>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Render Setting Lists */}
                {visibleSections?.map(section => (
                  <SettingList
                    key={section.id}
                    id={section.id}
                    title={section.title}
                    icon={section.icon}
                    settings={section.settings}
                    onEditClick={handleEditClick}
                  />
                ))}
              </div>

              <div className='col-xl-3'>
                <NavScrollTo>
                  <nav className='nav'>
                    <a className='nav-link' href='#languages' data-toggle='scroll-to'>
                      {t('SystemLanguage')}
                    </a>
                    {visibleSections?.map(section => (
                      <a key={section.id} className='nav-link' href={`#${section.id}`} data-toggle='scroll-to'>
                        {t(section.title)}
                      </a>
                    ))}
                  </nav>
                </NavScrollTo>

                {/* <NavScrollTo>
                  <nav className='nav'>
                    <a className='nav-link' href='#hr' data-toggle='scroll-to'>
                      {t('HumanResources')}
                    </a>
                    <a className='nav-link' href='#AME' data-toggle='scroll-to'>
                      {t('ApprovalsManagement')}
                    </a>
                    <a className='nav-link' href='#SU' data-toggle='scroll-to'>
                      {t('SupportingUnits')}
                    </a>
                    <a className='nav-link' href='#FLEET' data-toggle='scroll-to'>
                      {t('FleetManagement')}
                    </a>
                    <a className='nav-link' href='#NTX' data-toggle='scroll-to'>
                      {t('GeneralSystem')}
                    </a>
                    <a className='nav-link' href='#languages' data-toggle='scroll-to'>
                      {t('SystemLanguage')}
                    </a>
                  </nav>
                </NavScrollTo> */}
              </div>
            </div>
          </Loading>
        </div>
      </div>

      <NeatixModal centered show={showModal} setShow={setShowModal} size='md' bodyClassName='p-0' showHeader={false}>
        <Card>
          <CardBody>
            {settingId && (
              <UpdateNtxSettings settingId={settingId} settingType={settingType} onClose={() => setShowModal(false)} />
            )}
          </CardBody>
        </Card>
      </NeatixModal>

      <ModalComponent id='modalLanguage' title={t('SystemLanguage')} size='sm'>
        <NtxLanguage />
      </ModalComponent>
    </div>
  );
};

const getSettingType = (item: any): string => {
  return item.formatType === 'S' && ['NUMBER', 'NOTE', 'TEXT', 'DATE'].includes(item.validationType)
    ? 'input'
    : item.formatType === 'D' && item.validationType === 'S'
    ? 'radio'
    : item.formatType === 'S' && item.validationType === 'BOOLEAN'
    ? 'checkbox'
    : '';
};

interface SettingListProps {
  id: string;
  title: string;
  icon: string;
  settings: any[];
  onEditClick: (id: string, type: 'radio' | 'text') => void;
}

const SettingList: React.FC<SettingListProps> = ({ title, icon, settings, onEditClick, id }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const getDisplayValue = (item: any) => {
    const settingType = getSettingType(item);

    if (settingType === 'radio') {
      const valueList = item.getFlexValueList || [];
      const matchedItem = valueList.find((f: any) => f?.id?.toLowerCase() === item?.value?.toLowerCase());
      return language === 'ar' ? matchedItem?.name2 || item.value : matchedItem?.name || item.value;
    } else if (settingType === 'checkbox') {
      return item.value === '1' ? t('Yes') : t('No');
    } else {
      return item.value || ''; // For 'input' or unknown types
    }
  };

  return (
    <div id={id} className='mb-5'>
      <h5>
        <i className={`${icon} text-theme mb-1`}></i> {t(title)}
      </h5>

      <Card>
        <div className='list-group list-group-flush'>
          {settings?.length > 0 ? (
            settings.map((item: any) => (
              <div id={item.id} className='list-group-item d-flex align-items-center' key={item.id}>
                <div className='flex-1 text-break'>
                  <div className='text-inverse text-opacity-100'>{language === 'ar' ? item.name2 : item.name}</div>
                  <div className='text-inverse text-opacity-50 small'>
                    {language === 'ar' ? item.description2 : item.description}
                  </div>
                </div>
                <div className='flex-1 text-center text-break bg-body rounded-1 p-1 me-2 shadow border border-theme'>
                  <div className='text-theme'>{getDisplayValue(item)}</div>
                </div>
                <div>
                  <Link
                    to=''
                    className='btn btn-sm btn-theme w-100px'
                    // onClick={() => onEditClick(item.id, item.getFlexValueList?.length === 0 ? 'text' : 'radio')}
                    onClick={() => onEditClick(item.id, item)}
                  >
                    {t('Edit')}
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className='list-group-item text-center text-muted'>
              <i className='fas fa-cog me-2 text-theme'></i>
              {t(language === 'ar' ? 'لا توجد إعدادات متاحة' : 'No settings available')}
              <i className='fas fa-cog ms-2 text-theme'></i>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NtxSettings;

// const NtxSettings = () => {
//   const { HasPermission } = usePermissions();
//   const { t, i18n } = useTranslation();

//   const [showModal, setShowModal] = useState<boolean>(false);
//   const [settingId, setSettingId] = useState<string>();
//   const [settingType, setSettingType] = useState<any>('');
//   const { HRSettingList, NTXSettingList, FLEETSettingList, FinanceSettingList, loading, error } = useGetNtxSettings();

//   const getDisplayValue = (item: any, language: string) => {
//     const valueList = item.getFlexValueList || [];

//     if (valueList.length > 0) {
//       const matchedItem = valueList.find((f: any) => f?.id?.toLowerCase() === item?.value?.toLowerCase());

//       return language === 'ar' ? matchedItem?.name2 || item.value : matchedItem?.name || item.value;
//     }

//     return item.value;
//   };

//   const handleEditClick = async (id: string, type: 'radio' | 'text') => {
//     await setSettingId(id);
//     await setSettingType(type);
//     setShowModal(true);
//   };

//   return (
//     <>
//       <Loading loading={loading} error={error}>
//         <>
//           <div className='container p-0'>
//             <div className='row justify-content-center p-0'>
//               <div className='col-xl-10'>
//                 <div className='row'>
//                   <div className='col-xl-9'>
//                     <div id='languages' className='mb-5'>
//                       <h5>
//                         <i className='fa fa-language fa-fw text-theme'></i> {t('SystemLanguage')}
//                       </h5>
//                       <p> </p>
//                       <Card>
//                         <div className='list-group list-group-flush'>
//                           <div className='list-group-item d-flex align-items-center'>
//                             <div className='flex-1 text-break'>
//                               <div> {t('Language')}</div>
//                               <div className='text-inverse text-opacity-50'>
//                                 {t('English')} , {t('Arabic')} , {t('Turkish')}
//                               </div>
//                             </div>

//                             <div className='flex-1 text-center text-break bg-inverse bg-opacity-10 rounded-5 p-1 me-2 shadow border-bottom border-theme'>
//                               <div className=' text-theme'>{i18n.language === 'ar' ? t('Arabic') : t('English')}</div>
//                             </div>

//                             <div>
//                               <a
//                                 href='#modalLanguage'
//                                 data-bs-toggle='modal'
//                                 className='btn btn-outline-default w-100px'
//                               >
//                                 {t('Change')}
//                               </a>
//                             </div>
//                           </div>
//                         </div>
//                       </Card>
//                     </div>

//                     {/* {HasPermission('NtxViewSettingList') && ( */}
//                     {true && (
//                       <>
//                         <div id='hr' className='mb-5'>
//                           <h5>
//                             <i className='far fa-user fa-fw text-theme'></i> {t('HumanResources')}
//                           </h5>
//                           <p></p>
//                           <Card>
//                             <div className='list-group list-group-flush'>
//                               {HRSettingList?.map((item: any) => (
//                                 <div className='list-group-item d-flex align-items-center' key={item.id}>
//                                   <div className='flex-1 text-break'>
//                                     <div className='text-inverse text-opacity-100'>
//                                       {i18n.language === 'ar' ? item.name2 : item.name}
//                                     </div>
//                                     <div className='text-inverse text-opacity-50'>
//                                       {i18n.language === 'ar' ? item.description2 : item.description}
//                                     </div>
//                                   </div>
//                                   <div className='flex-1 text-center text-break bg-inverse bg-opacity-10 rounded-3 p-1 me-2 shadow border-bottom border-theme '>
//                                     <div className=' text-theme'>{getDisplayValue(item, i18n.language)}</div>
//                                   </div>

//                                   {/* {HasPermission('NtxEditSetting') && ( */}
//                                   {true && (
//                                     <div>
//                                       <Link
//                                         to='#modalEdit'
//                                         className='btn btn-outline-default w-100px'
//                                         onClick={() =>
//                                           handleEditClick(
//                                             item.id,
//                                             item.getFlexValueList?.length === 0 ? 'text' : 'radio'
//                                           )
//                                         }
//                                       >
//                                         {t('Edit')}
//                                       </Link>
//                                     </div>
//                                   )}
//                                 </div>
//                               ))}
//                             </div>
//                           </Card>
//                         </div>

//                         <div id='NTX' className='mb-5'>
//                           <h5>
//                             <i className='far fa-user fa-fw text-theme'></i> {t('GeneralSystem')}
//                           </h5>
//                           <p></p>
//                           <Card>
//                             <div className='list-group list-group-flush'>
//                               {NTXSettingList?.map((item: any) => (
//                                 <div className='list-group-item d-flex align-items-center' key={item.id}>
//                                   <div className='flex-1 text-break'>
//                                     <div className='text-inverse text-opacity-100'>
//                                       {i18n.language === 'ar' ? item.name2 : item.name}
//                                     </div>
//                                     <div className='text-inverse text-opacity-50'>
//                                       {i18n.language === 'ar' ? item.description2 : item.description}
//                                     </div>
//                                   </div>
//                                   <div className='flex-1 text-center text-break bg-inverse bg-opacity-10 rounded-3 p-1 me-2 shadow border-bottom border-theme '>
//                                     <div className=' text-theme'>{getDisplayValue(item, i18n.language)}</div>
//                                   </div>

//                                   {/* {HasPermission('NtxEditSetting') && ( */}
//                                   {true && (
//                                     <div>
//                                       <Link
//                                         to=''
//                                         className='btn btn-outline-default w-100px'
//                                         onClick={() =>
//                                           handleEditClick(
//                                             item.id,
//                                             item.getFlexValueList?.length === 0 ? 'text' : 'radio'
//                                           )
//                                         }
//                                       >
//                                         {t('Edit')}
//                                       </Link>
//                                     </div>
//                                   )}
//                                 </div>
//                               ))}
//                             </div>
//                           </Card>
//                         </div>

//                         <div id='FLEET' className='mb-5'>
//                           <h5>
//                             <i className='far fa-user fa-fw text-theme'></i> {t('FleetManagement')}
//                           </h5>
//                           <p></p>
//                           <Card>
//                             <div className='list-group list-group-flush'>
//                               {FLEETSettingList?.map((item: any) => (
//                                 <div className='list-group-item d-flex align-items-center' key={item.id}>
//                                   <div className='flex-1 text-break'>
//                                     <div className='text-inverse text-opacity-100'>
//                                       {i18n.language === 'ar' ? item.name2 : item.name}
//                                     </div>
//                                     <div className='text-inverse text-opacity-50'>
//                                       {i18n.language === 'ar' ? item.description2 : item.description}
//                                     </div>
//                                   </div>
//                                   <div className='flex-1 text-center text-break bg-inverse bg-opacity-10 rounded-3 p-1 me-2 shadow border-bottom border-theme '>
//                                     <div className=' text-theme'>
//                                       {/* {i18n.language === 'ar'
//                                         ? item.getFlexValueList?.find(
//                                             (f: any) =>
//                                               f?.id?.toLowerCase() === item?.value?.toLowerCase()
//                                           )?.name2
//                                         : item.getFlexValueList?.find(
//                                             (f: any) =>
//                                               f?.id?.toLowerCase() === item?.value?.toLowerCase()
//                                           )?.name || item.value} */}
//                                       {getDisplayValue(item, i18n.language)}
//                                     </div>
//                                   </div>

//                                   {/* {HasPermission('NtxEditSetting') && ( */}
//                                   {true && (
//                                     <div>
//                                       <Link
//                                         to=''
//                                         className='btn btn-outline-default w-100px'
//                                         onClick={() =>
//                                           handleEditClick(
//                                             item.id,
//                                             item.getFlexValueList?.length === 0 ? 'text' : 'radio'
//                                           )
//                                         }
//                                       >
//                                         {t('Edit')}
//                                       </Link>
//                                     </div>
//                                   )}
//                                 </div>
//                               ))}
//                             </div>
//                           </Card>
//                         </div>

//                         <div id='Finance' className='mb-5'>
//                           <h5>
//                             <i className='far fa-user fa-fw text-theme'></i> {t('GeneralSystem')}
//                           </h5>
//                           <p></p>
//                           <Card>
//                             <div className='list-group list-group-flush'>
//                               {FinanceSettingList?.map((item: any) => (
//                                 <div className='list-group-item d-flex align-items-center' key={item.id}>
//                                   <div className='flex-1 text-break'>
//                                     <div className='text-inverse text-opacity-100'>
//                                       {i18n.language === 'ar' ? item.name2 : item.name}
//                                     </div>
//                                     <div className='text-inverse text-opacity-50'>
//                                       {i18n.language === 'ar' ? item.description2 : item.description}
//                                     </div>
//                                   </div>
//                                   <div className='flex-1 text-center text-break bg-inverse bg-opacity-10 rounded-3 p-1 me-2 shadow border-bottom border-theme '>
//                                     <div className=' text-theme'>{getDisplayValue(item, i18n.language)}</div>
//                                   </div>

//                                   {/* {HasPermission('NtxEditSetting') && ( */}
//                                   {true && (
//                                     <div>
//                                       <Link
//                                         to=''
//                                         className='btn btn-outline-default w-100px'
//                                         onClick={() =>
//                                           handleEditClick(
//                                             item.id,
//                                             item.getFlexValueList?.length === 0 ? 'text' : 'radio'
//                                           )
//                                         }
//                                       >
//                                         {t('Edit')}
//                                       </Link>
//                                     </div>
//                                   )}
//                                 </div>
//                               ))}
//                             </div>
//                           </Card>
//                         </div>
//                       </>
//                     )}
//                   </div>

//                   <div className='col-xl-3'>
//                     <NavScrollTo>
//                       <nav className='nav'>
//                         <a className='nav-link' href='#hr' data-toggle='scroll-to'>
//                           {t('HumanResources')}
//                         </a>
//                         <a className='nav-link' href='#notifications' data-toggle='scroll-to'>
//                           {t('ApprovalsManagement')}
//                         </a>
//                         <a className='nav-link' href='#privacyAndSecurity' data-toggle='scroll-to'>
//                           {t('SupportingUnits')}
//                         </a>
//                         <a className='nav-link' href='#FLEET' data-toggle='scroll-to'>
//                           {t('FleetManagement')}
//                         </a>
//                         <a className='nav-link' href='#shipping' data-toggle='scroll-to'>
//                           {t('GeneralSystem')}
//                         </a>
//                         <a className='nav-link' href='#languages' data-toggle='scroll-to'>
//                           {t('SystemLanguage')}
//                         </a>
//                         {/* <a className="nav-link" href="#system" data-toggle="scroll-to">System</a> */}
//                       </nav>
//                     </NavScrollTo>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* <ModalComponent
//             id="modalEdit"
//             title={
//               i18n.language === 'ar'
//                 ? SettingsLiat?.find((f) => f.id === settingId)?.name2
//                 : SettingsLiat?.find((f) => f.id === settingId)?.name
//             }>
//             {settingId && <UpdateNtxSettings settingId={settingId} settingType={settingType} />}
//           </ModalComponent> */}

//           <NeatixModal show={showModal} setShow={setShowModal} size='md' bodyClassName='p-0' showHeader={false}>
//             <Card>
//               <CardBody>
//                 {settingId && (
//                   <UpdateNtxSettings
//                     settingId={settingId}
//                     settingType={settingType}
//                     onClose={() => setShowModal(false)}
//                   />
//                 )}
//               </CardBody>
//             </Card>
//           </NeatixModal>

//           <ModalComponent id='modalLanguage' title={t('SystemLanguage')} size='sm'>
//             <NtxLanguage />
//           </ModalComponent>
//         </>
//       </Loading>
//     </>
//   );
// };

// const settingSections = [
//   { id: 'NTX', title: 'GeneralSystem', icon: 'far fa-user fa-fw', settings: NTXSettingList },
//   { id: 'hr', title: 'HumanResources', icon: 'far fa-user fa-fw', settings: HRSettingList },
//   { id: 'FLEET', title: 'FleetManagement', icon: 'far fa-user fa-fw', settings: FLEETSettingList },
//   { id: 'Finance', title: 'GeneralSystem', icon: 'far fa-user fa-fw', settings: FinanceSettingList },
//   { id: 'AME', title: 'ApprovalsManagement', icon: 'far fa-user fa-fw', settings: AMESettingList },
//   { id: 'RAQEEB', title: 'attendanceManagement', icon: 'far fa-user fa-fw', settings: AttendanceSettingList },

//   { id: 'FA', title: 'assetsManagement', icon: 'far fa-user fa-fw', settings: FixedAssetSettingList },
//   { id: 'INV', title: 'inventoryManagement', icon: 'far fa-user fa-fw', settings: InventorySettingList },
//   { id: 'NFT', title: 'notificationsSystem', icon: 'far fa-user fa-fw', settings: NotificationsSettingList },
//   { id: 'Payables', title: 'payablesManagement', icon: 'far fa-user fa-fw', settings: PayablesSettingList },
//   { id: 'Payroll', title: 'payrollManagement', icon: 'far fa-user fa-fw', settings: PayrollSettingList },
//   { id: 'Receivables', title: 'receivablesManagement', icon: 'far fa-user fa-fw', settings: ReceivablesSettingList },
//   { id: 'Reports', title: 'ApprovalsManagement', icon: 'far fa-user fa-fw', settings: ReportsSettingList },
//   { id: 'SU', title: 'supportingUnits', icon: 'far fa-user fa-fw', settings: SUSettingList },
//   { id: 'Sales', title: 'salesManagement', icon: 'far fa-user fa-fw', settings: SalesSettingList },
//   { id: 'POS', title: 'posManagement', icon: 'far fa-user fa-fw', settings: PosSettingList }
// ];

//   const settingSections = [
//   { id: 'NTX', title: 'GeneralSystem', icon: 'fas fa-cogs fa-fw', settings: NTXSettingList },
//   { id: 'hr', title: 'HumanResources', icon: 'fas fa-users fa-fw', settings: HRSettingList },
//   { id: 'FLEET', title: 'FleetManagement', icon: 'fas fa-truck fa-fw', settings: FLEETSettingList },
//   { id: 'Finance', title: 'GeneralSystem', icon: 'fas fa-dollar-sign fa-fw', settings: FinanceSettingList },
//   { id: 'AME', title: 'ApprovalsManagement', icon: 'fas fa-check-circle fa-fw', settings: AMESettingList },
//   { id: 'RAQEEB', title: 'attendanceManagement', icon: 'fas fa-clock fa-fw', settings: AttendanceSettingList },
//   { id: 'FA', title: 'assetsManagement', icon: 'fas fa-building fa-fw', settings: FixedAssetSettingList },
//   { id: 'INV', title: 'inventoryManagement', icon: 'fas fa-warehouse fa-fw', settings: InventorySettingList },
//   { id: 'NFT', title: 'notificationsSystem', icon: 'fas fa-bell fa-fw', settings: NotificationsSettingList },
//   { id: 'Payables', title: 'payablesManagement', icon: 'fas fa-money-check-alt fa-fw', settings: PayablesSettingList },
//   { id: 'Payroll', title: 'payrollManagement', icon: 'fas fa-file-invoice-dollar fa-fw', settings: PayrollSettingList },
//   { id: 'Receivables', title: 'receivablesManagement', icon: 'fas fa-hand-holding-usd fa-fw', settings: ReceivablesSettingList },
//   { id: 'Reports', title: 'ApprovalsManagement', icon: 'fas fa-chart-bar fa-fw', settings: ReportsSettingList },
//   { id: 'SU', title: 'supportingUnits', icon: 'fas fa-tools fa-fw', settings: SUSettingList },
//   { id: 'Sales', title: 'salesManagement', icon: 'fas fa-shopping-cart fa-fw', settings: SalesSettingList },
//   { id: 'POS', title: 'posManagement', icon: 'fas fa-cash-register fa-fw', settings: PosSettingList }
// ];

// const settingSections = [
//   { id: 'NTX', title: 'GeneralSystem', icon: 'fas fa-cogs', settings: NTXSettingList },
//   { id: 'hr', title: 'HumanResources', icon: 'fas fa-users', settings: HRSettingList },
//   { id: 'Payroll', title: 'payrollManagement', icon: 'fas fa-money-check-alt', settings: PayrollSettingList },
//   { id: 'RAQEEB', title: 'attendanceManagement', icon: 'fas fa-user-clock', settings: AttendanceSettingList },
//   { id: 'AME', title: 'ApprovalsManagement', icon: 'fas fa-check-circle', settings: AMESettingList },
//   { id: 'Sales', title: 'salesManagement', icon: 'fas fa-shopping-cart', settings: SalesSettingList },
//   { id: 'INV', title: 'inventoryManagement', icon: 'fas fa-warehouse', settings: InventorySettingList },
//   { id: 'Finance', title: 'financeManagement', icon: 'fas fa-chart-line', settings: FinanceSettingList },
//   { id: 'Payables', title: 'payablesManagement', icon: 'fas fa-credit-card', settings: PayablesSettingList },
//   {
//     id: 'Receivables',
//     title: 'receivablesManagement',
//     icon: 'fas fa-hand-holding-usd',
//     settings: ReceivablesSettingList
//   },
//   { id: 'FA', title: 'assetsManagement', icon: 'fas fa-boxes', settings: FixedAssetSettingList },
//   { id: 'POS', title: 'posManagement', icon: 'fas fa-cash-register', settings: PosSettingList },
//   { id: 'FLEET', title: 'FleetManagement', icon: 'fas fa-truck-moving', settings: FLEETSettingList },
//   { id: 'SU', title: 'supportingUnits', icon: 'fas fa-toolbox', settings: SUSettingList },

//   { id: 'NFT', title: 'notificationsSystem', icon: 'fas fa-bell', settings: NotificationsSettingList },
//   { id: 'Reports', title: 'reportsManagement', icon: 'fas fa-file-alt', settings: ReportsSettingList }
// ];
